import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  authApiClient,
  bxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { QueryStringParameters } from "./url-builder";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import { ConfigJson } from "./types";

export const sharedApi = {
  getDashboardData: async (
    tenantId: string,
    dashboardParameter: string,
    utilityId: string,
    queryParams?: Record<string, any>,
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "dashboard-metric-snapshot",
      (query) => {
        query.push("tenant_id", tenantId);
        query.push("dashboard_parameter", dashboardParameter);
        query.push("utility_id", utilityId);

        if (queryParams && typeof queryParams === "object") {
          Object.entries(queryParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              query.push(key, String(value));
            }
          });
        }

        return query;
      },
    );

    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },

  downloadBillTemplate: async (billId: string): Promise<Blob> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "bx",
      `render-dynamic-template/${billId}`,
    );
    const response = await bxApiClient.get(url, {
      responseType: "blob",
    });
    return response.data;
  },
};

export const getTerritoryConfigMap = async (
  configLevel: "zone" | "division" | "area" | "sub_area" | "premise" = "zone",
  codeList?: string | string[],
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `config-map/territory_json`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("config_level", configLevel);

      if (codeList) {
        if (Array.isArray(codeList)) {
          codeList.forEach((code) => {
            qs.push("code_list", code);
          });
        } else {
          qs.push("code_list", codeList);
        }
      }
    },
  );

  const response = await onboardingApiClient.get(url);
  return response.data?.result;
};

export const getUtilityServicesOptions = async (
  utilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "auth",
    "utility/services",
    (query) => {
      query.push("utility_id", utilityId);
    }
  );
  const response = await authApiClient.get(url); // Use appropriate client
  return response.data.result;
};

export const getConfigMapJson = async ({
  jsonType,
  remoteUtilityId,
  configLevel,
  codeLevel,
}: {
  jsonType: "category_json" | "territory_json";
  remoteUtilityId: string;
  configLevel: string;
  codeLevel?: string;
}): Promise<ConfigJson[]> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "onboarding",
    `config-map`,
    [jsonType],
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      if (configLevel !== "sub_category" || codeLevel) {
        query.push("config_level", configLevel);
      }
      if (codeLevel) {
        query.push("code_list", codeLevel);
      }
    },
  );

  const response = await onboardingApiClient.get(url);
  const tResponse = transformJsonToObject<ConfigJson[]>(response.data.result);
  return tResponse;
};
